import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';

const CHAIN_OPTIONS = [
  { id: 'evm', label: 'EVM', emoji: '⟠', hint: 'Base · Ethereum · BSC · Robinhood' },
  { id: 'robinhood', label: 'Robinhood only', emoji: '🪶', hint: 'Robinhood Chain only' },
  { id: 'solana', label: 'Solana', emoji: '◎', hint: 'Solana mainnet' },
];

const EVM_SUB_CHAINS = ['base', 'ethereum', 'bsc', 'robinhood'];

const EXPLORER = {
  solana: {
    account: (a) => `https://solscan.io/account/${a}`,
    tx: (s) => `https://solscan.io/tx/${s}`,
  },
  evm: {
    account: (a) => `https://debank.com/profile/${a}`,
    tx: (s, chain) => {
      const c = chain && EXPLORER[chain] ? chain : 'base';
      return (EXPLORER[c] || EXPLORER.base).tx(s);
    },
  },
  base: {
    account: (a) => `https://basescan.org/address/${a}`,
    tx: (s) => `https://basescan.org/tx/${s}`,
  },
  ethereum: {
    account: (a) => `https://etherscan.io/address/${a}`,
    tx: (s) => `https://etherscan.io/tx/${s}`,
  },
  bsc: {
    account: (a) => `https://bscscan.com/address/${a}`,
    tx: (s) => `https://bscscan.com/tx/${s}`,
  },
  robinhood: {
    account: (a) => `https://robinhoodchain.blockscout.com/address/${a}`,
    tx: (s) => `https://robinhoodchain.blockscout.com/tx/${s}`,
  },
};

function walletChain(w) {
  const c = w?.chain || 'solana';
  if (c === 'robinhood') return 'robinhood';
  if (c === 'evm' || EVM_SUB_CHAINS.includes(c)) return 'evm';
  return c;
}

function chainLabel(chainId) {
  const opt = CHAIN_OPTIONS.find((c) => c.id === chainId);
  if (opt) return `${opt.emoji} ${opt.label}`;
  return chainId;
}

export default function WalletMonitor() {
  const [wallets, setWallets] = useState([]);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tokensAdded, setTokensAdded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('wallets');

  // Add wallet form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWallet, setNewWallet] = useState({
    walletAddress: '',
    label: '',
    chain: 'evm',
    minMarketCap: 10000,
    maxMarketCap: 500000
  });
  const [addLoading, setAddLoading] = useState(false);

  // Chart modal state
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });

  // Edit token modal state
  const [editingToken, setEditingToken] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Edit wallet modal state
  const [editingWallet, setEditingWallet] = useState(null);
  const [editWalletLoading, setEditWalletLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [walletsRes, statsRes, transactionsRes, tokensRes] = await Promise.all([
        api.get('/wallet-monitor/wallets'),
        api.get('/wallet-monitor/stats'),
        api.get('/wallet-monitor/transactions?limit=50'),
        api.get('/wallet-monitor/tokens-added?limit=20')
      ]);

      setWallets(walletsRes.data.wallets || []);
      setStats(statsRes.data.stats || null);
      setTransactions(transactionsRes.data.transactions || []);
      setTokensAdded(tokensRes.data.tokens || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch wallet monitor data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleToggleWallet = async (wallet) => {
    try {
      const chain = walletChain(wallet);
      await api.post(`/wallet-monitor/wallets/${encodeURIComponent(wallet.walletAddress)}/toggle?chain=${chain}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle wallet');
    }
  };

  const handleDeleteWallet = async (wallet) => {
    if (!window.confirm('Are you sure you want to remove this wallet from monitoring?')) {
      return;
    }
    try {
      const chain = walletChain(wallet);
      await api.delete(`/wallet-monitor/wallets/${encodeURIComponent(wallet.walletAddress)}?chain=${chain}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete wallet');
    }
  };

  const handleAddWallet = async (e) => {
    e.preventDefault();
    try {
      setAddLoading(true);
      await api.post('/wallet-monitor/wallets', newWallet);
      setNewWallet({
        walletAddress: '',
        label: '',
        chain: 'evm',
        minMarketCap: 10000,
        maxMarketCap: 500000
      });
      setShowAddForm(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add wallet');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteToken = async (tokenId, tokenSymbol) => {
    if (!window.confirm(`Are you sure you want to remove ${tokenSymbol} from tracking?`)) {
      return;
    }
    try {
      await api.delete(`/tokens/${tokenId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete token');
    }
  };

  const handleUpdateToken = async (e) => {
    e.preventDefault();
    if (!editingToken) return;

    try {
      setEditLoading(true);
      await api.put(`/tokens/${editingToken._id}`, {
        symbol: editingToken.symbol,
        name: editingToken.name,
        initialMarketCap: editingToken.initialMarketCap,
        paused: editingToken.paused
      });
      setEditingToken(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update token');
    } finally {
      setEditLoading(false);
    }
  };

  const handlePauseToken = async (tokenId, currentPaused) => {
    try {
      await api.put(`/tokens/${tokenId}`, { paused: !currentPaused });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update token');
    }
  };

  const handleUpdateWallet = async (e) => {
    e.preventDefault();
    if (!editingWallet) return;

    try {
      setEditWalletLoading(true);
      await api.put(`/wallet-monitor/wallets/${encodeURIComponent(editingWallet.walletAddress)}?chain=${walletChain(editingWallet)}`, {
        label: editingWallet.label,
        minMarketCap: editingWallet.minMarketCap,
        maxMarketCap: editingWallet.maxMarketCap,
        isActive: editingWallet.isActive
      });
      setEditingWallet(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update wallet');
    } finally {
      setEditWalletLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  if (loading && wallets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading wallet monitor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Wallet Monitor</h1>
          <p className="text-gray-600">Track wallet buys on Solana, EVM (Base, Ethereum, BSC), or Robinhood — auto-add tokens to milestone tracking</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showAddForm ? 'Cancel' : '+ Add Wallet'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Add Wallet Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Add New Wallet</h3>
          <form onSubmit={handleAddWallet} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Chain *
                </label>
                <div className="flex flex-wrap gap-3">
                  {CHAIN_OPTIONS.map((c) => {
                    const selected = newWallet.chain === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setNewWallet({ ...newWallet, chain: c.id })}
                        className={`flex-1 min-w-[140px] px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                          selected
                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500/30'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {c.emoji} {c.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Wallet Address *
                </label>
                <input
                  type="text"
                  value={newWallet.walletAddress}
                  onChange={(e) => setNewWallet({ ...newWallet, walletAddress: e.target.value })}
                  placeholder={
                    newWallet.chain === 'solana'
                      ? 'Solana address'
                      : newWallet.chain === 'robinhood'
                        ? '0x… (Robinhood Chain)'
                        : '0x… (scans Base, ETH, BSC, Robinhood)'
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={newWallet.label}
                  onChange={(e) => setNewWallet({ ...newWallet, label: e.target.value })}
                  placeholder="Optional nickname"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Min Market Cap ($)
                </label>
                <input
                  type="number"
                  value={newWallet.minMarketCap}
                  onChange={(e) => setNewWallet({ ...newWallet, minMarketCap: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Max Market Cap ($)
                </label>
                <input
                  type="number"
                  value={newWallet.maxMarketCap}
                  onChange={(e) => setNewWallet({ ...newWallet, maxMarketCap: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={addLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {addLoading ? 'Adding...' : 'Add Wallet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Wallets</div>
            <div className="text-2xl font-bold text-blue-600">{stats.totalWallets}</div>
            <div className="text-xs text-gray-400 dark:text-gray-500">{stats.activeWallets} active</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Buys Detected</div>
            <div className="text-2xl font-bold text-purple-600">{stats.totalBuysDetected}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Tokens Added</div>
            <div className="text-2xl font-bold text-green-600">{stats.totalTokensAdded}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Transactions</div>
            <div className="text-2xl font-bold text-orange-600">{stats.totalTransactionsRecorded}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {['wallets', 'tokens', 'transactions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'wallets' && `Monitored Wallets (${wallets.length})`}
              {tab === 'tokens' && `Tokens Added (${tokensAdded.length})`}
              {tab === 'transactions' && `Recent Transactions (${transactions.length})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {activeTab === 'wallets' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Chain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Label</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">MC Range</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Buys</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tokens Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Check</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {wallets.map((wallet) => (
                  <tr key={`${walletChain(wallet)}:${wallet.walletAddress}`} className="hover:bg-gray-50 dark:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>{chainLabel(walletChain(wallet))}</div>
                      {walletChain(wallet) === 'evm' && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">Base · ETH · BSC · RH</div>
                      )}
                      {walletChain(wallet) === 'robinhood' && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">Robinhood only</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        wallet.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 dark:text-gray-100'
                      }`}>
                        {wallet.isActive ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {wallet.label || 'Unnamed'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      <a
                        href={(EXPLORER[walletChain(wallet)] || EXPLORER.solana).account(wallet.walletAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {truncateAddress(wallet.walletAddress)}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      ${formatNumber(wallet.minMarketCap)} - ${formatNumber(wallet.maxMarketCap)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {wallet.totalBuysDetected || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {wallet.totalTokensAdded || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {wallet.lastCheckedAt ? new Date(wallet.lastCheckedAt).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => setEditingWallet({ ...wallet })}
                        className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 dark:text-blue-300 hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleWallet(wallet)}
                        className={`px-2 py-1 rounded text-xs ${
                          wallet.isActive
                            ? 'bg-yellow-100 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 dark:text-green-300 hover:bg-green-200'
                        }`}
                      >
                        {wallet.isActive ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        onClick={() => handleDeleteWallet(wallet)}
                        className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {wallets.length === 0 && (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No wallets being monitored. Add a wallet to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'tokens' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Initial MC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Multiplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {tokensAdded.map((token) => (
                  <tr key={token._id} className={`hover:bg-gray-50 dark:bg-gray-800/50 ${token.paused ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        token.paused ? 'bg-gray-100 text-gray-800 dark:text-gray-100' : 'bg-green-100 text-green-800'
                      }`}>
                        {token.paused ? 'Paused' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {token.name} ({token.symbol})
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      <button
                        onClick={() => setChartModal({
                          isOpen: true,
                          chain: token.chain || 'solana',
                          tokenAddress: token.address,
                          tokenSymbol: token.symbol
                        })}
                        className="text-blue-600 hover:underline"
                      >
                        {truncateAddress(token.address)}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      ${formatNumber(token.initialMarketCap)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {token.currentMultiplier > 0 ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          token.currentMultiplier >= 10 ? 'bg-green-100 text-green-800 dark:text-green-300' :
                          token.currentMultiplier >= 5 ? 'bg-blue-100 text-blue-800 dark:text-blue-300' :
                          token.currentMultiplier >= 2 ? 'bg-yellow-100 text-yellow-800 dark:text-yellow-300' :
                          'bg-gray-100 text-gray-800 dark:text-gray-100'
                        }`}>
                          {token.currentMultiplier}x
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {token.addedBy?.replace('WalletMonitor:', '')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(token.addedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => setChartModal({
                          isOpen: true,
                          chain: token.chain || 'solana',
                          tokenAddress: token.address,
                          tokenSymbol: token.symbol
                        })}
                        className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 dark:text-green-300 hover:bg-green-200"
                      >
                        Chart
                      </button>
                      <button
                        onClick={() => setEditingToken({ ...token })}
                        className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 dark:text-blue-300 hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handlePauseToken(token._id, token.paused)}
                        className={`px-2 py-1 rounded text-xs ${
                          token.paused
                            ? 'bg-green-100 text-green-800 dark:text-green-300 hover:bg-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200'
                        }`}
                      >
                        {token.paused ? 'Resume' : 'Pause'}
                      </button>
                      <button
                        onClick={() => handleDeleteToken(token._id, token.symbol)}
                        className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {tokensAdded.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No tokens added by wallet monitor yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Wallet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Market Cap</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Signature</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        tx.transactionType === 'buy' ? 'bg-green-100 text-green-800 dark:text-green-300' : 'bg-red-100 text-red-800'
                      }`}>
                        {tx.transactionType?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {tx.walletLabel || truncateAddress(tx.walletAddress)}
                      {tx.chain && tx.chain !== 'solana' && tx.chain !== 'evm' && (
                        <span className="ml-1 text-xs text-gray-400">({tx.chain})</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {tx.tokenSymbol || truncateAddress(tx.tokenAddress)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      ${formatNumber(tx.marketCapAtTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tx.addedToTracking ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(tx.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-xs">
                      <a
                        href={(EXPLORER[tx.chain || 'solana'] || EXPLORER.solana).tx(tx.signature, tx.chain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {truncateAddress(tx.signature)}
                      </a>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No transactions recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />

      {/* Edit Token Modal */}
      {editingToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Token</h3>
            <form onSubmit={handleUpdateToken} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Symbol</label>
                <input
                  type="text"
                  value={editingToken.symbol}
                  onChange={(e) => setEditingToken({ ...editingToken, symbol: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Name</label>
                <input
                  type="text"
                  value={editingToken.name}
                  onChange={(e) => setEditingToken({ ...editingToken, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Initial Market Cap ($)</label>
                <input
                  type="number"
                  value={editingToken.initialMarketCap || ''}
                  onChange={(e) => setEditingToken({ ...editingToken, initialMarketCap: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="paused"
                  checked={editingToken.paused || false}
                  onChange={(e) => setEditingToken({ ...editingToken, paused: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="paused" className="text-sm text-gray-700 dark:text-gray-300">Paused</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingToken(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Wallet Modal */}
      {editingWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Wallet</h3>
            <form onSubmit={handleUpdateWallet} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Wallet Address</label>
                <input
                  type="text"
                  value={editingWallet.walletAddress}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Label</label>
                <input
                  type="text"
                  value={editingWallet.label || ''}
                  onChange={(e) => setEditingWallet({ ...editingWallet, label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Min Market Cap ($)</label>
                  <input
                    type="number"
                    value={editingWallet.minMarketCap || ''}
                    onChange={(e) => setEditingWallet({ ...editingWallet, minMarketCap: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Max Market Cap ($)</label>
                  <input
                    type="number"
                    value={editingWallet.maxMarketCap || ''}
                    onChange={(e) => setEditingWallet({ ...editingWallet, maxMarketCap: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="walletActive"
                  checked={editingWallet.isActive || false}
                  onChange={(e) => setEditingWallet({ ...editingWallet, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="walletActive" className="text-sm text-gray-700 dark:text-gray-300">Active (Monitoring)</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingWallet(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editWalletLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editWalletLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
